import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ArrowRightSmall } from '@peacecover/icon';
import { ButtonToggle } from '@peacecover/component';
import useUserQuery from '../hooks/queries/useUserQuery';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';
import TradeMethodBottomDrawer from '../../components/drawer/TradeMethodBottomDrawer';
import SelectedPhotoCardsList from './components/SelectedPhotoCardsList';
import AddTradingPhotoCardsV2 from '../../components/api/mutations/AddTradingPhotoCardsV2';
import { useSelectedTradingCardsStore } from './store/useSelectedTradingCardsStore';
import BottomCTA from './components/BottomCTA';
import RegisterLoadingPage from './RegisterLoadingPage';
import RegisterCompletedPage from './RegisterCompletedPage';

const SellPhotoCardsPage = () => {
  const navigate = useNavigate();
  const handleHistoryBack = useHandleHistoryBack();
  const [description, setDescription] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedTradeMethod, setSelectedTradeMethod] = useState<string | null>(null);
  const [isNewItem, setIsNewItem] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadCompleted, setIsUploadCompleted] = useState(false);

  const { selectedPhotoCards, clearPhotoCards } = useSelectedTradingCardsStore();
  const startNewCapture = () => {
    clearPhotoCards();
    navigate('/image-search?mode=sell');
  };
  const { data: user } = useUserQuery();

  if (!user) return null;

  const buildTradingPhotoCardsData = () =>
    selectedPhotoCards.map((card) => ({
      artistId: card.artistId,
      photoCard: {
        memberId: card.photoCard.memberId,
        photoCardId: card.photoCard.photoCardId,
        isNewItem
      },
      description,
      category: 'sale',
      price: card.price,
      tradeMethod: selectedTradeMethod
    }));

  const onDone = async () => {
    if (isUploading || !user?.tradingUserProfile) return;
    setIsUploading(true);
    try {
      const response = await AddTradingPhotoCardsV2(
        user.tradingUserProfile.userProfileId,
        buildTradingPhotoCardsData(),
        []
      );
      if (response.ok) {
        clearPhotoCards();
        setIsUploadCompleted(true);
      }
    } catch (error) {
      console.error('Error adding trading photo cards:', error);
    } finally {
      setIsUploading(false);
    }
  };

  if (isUploading) return <RegisterLoadingPage />;
  if (isUploadCompleted) return <RegisterCompletedPage />;

  return (
    <main className="min-h-screen bg-pure-white dark:bg-background-dark pt-[calc(env(safe-area-inset-top))] pb-[calc(env(safe-area-inset-bottom))]">
      <Helmet>
        <title>{t('ConfirmNewSalesTradingPhotoCardsPage.title')}</title>
      </Helmet>
      <header className="fixed top-0 left-0 right-0 z-10 mx-auto flex max-w-[var(--chakra-sizes-xl)] items-center bg-pure-white px-[16px] py-[8px] pt-[calc(env(safe-area-inset-top)+8px)] dark:bg-background-dark">
        <ArrowLeft className="fill-text-main-lv1 my-[2px]" onClick={handleHistoryBack} />
        <h1 className="h-[20px] flex items-center absolute left-1/2 transform -translate-x-1/2 text-[17px] font-bold text-text-main-lv1 my-[4px] leading-none text-center">
          {t('ConfirmNewSalesTradingPhotoCardsPage.title')}
        </h1>
      </header>

      <div className="mt-[54px] px-[24px] pt-[24px] pb-[16px]">
        <h2 className="h-[18px] text-[15px] font-semibold text-text-main-lv1 leading-none">
          {t('ConfirmNewSalesTradingPhotoCardsPage.subTitleProduct')}
        </h2>
      </div>

      {selectedPhotoCards.length > 0 ? (
        <SelectedPhotoCardsList user={user} />
      ) : (
        <div className="mx-[24px] mt-[2px] py-[28px] rounded-[12px] bg-surface-lv2 flex flex-col items-center justify-center gap-[13px] text-center">
          <p className="text-[15px] font-semibold text-text-main-lv2">
            {t('SellPhotoCardsPage.emptyStateDescriptionLine1')}
            <br />
            {t('SellPhotoCardsPage.emptyStateDescriptionLine2')}
          </p>
          <button
            type="button"
            onClick={startNewCapture}
            className="flex items-center justify-center gap-[4px] w-fit h-[28px] pl-[12px] pr-[8px] rounded-full bg-primary-light dark:bg-primary-dark text-on-primary text-[13px] font-medium leading-[16px]"
          >
            {t('SellPhotoCardsPage.captureButton')}
            <ArrowRightSmall size={14} />
          </button>
        </div>
      )}

      <div className="px-[24px] pt-[32px] pb-[7px]">
        <h2 className="h-[18px] text-[15px] font-semibold text-text-main-lv1 leading-none">
          {t('ConfirmNewSalesTradingPhotoCardsPage.subTitleDescription')}
        </h2>
      </div>
      <div className="px-[24px]">
        <textarea
          className="h-[120px] w-full p-[20px] text-text-main-lv2 bg-transparent resize-none text-[15px] font-medium outline-none rounded-[12px] border border-separator1 placeholder:text-placeholder-light dark:placeholder:text-placeholder-dark"
          placeholder={t('ConfirmNewSalesTradingPhotoCardsPage.descriptionPlaceholder') || ''}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="flex flex-row items-center gap-[8px] px-[20px] pt-[10px] pb-[24px]">
        <ButtonToggle
          type="check"
          size="lg"
          checked={isNewItem}
          onChange={() => setIsNewItem(!isNewItem)}
        />
        <p className="text-[15px] leading-none text-text-main-lv1">
          {t('ConfirmNewSalesTradingPhotoCardsPage.newItem')}
        </p>
      </div>

      <div className="flex items-center justify-between mx-[24px] py-[24px] h-[68px] mb-[84px] border-t border-surface-lv2">
        <p className="text-[15px] font-semibold text-text-main-lv1 leading-none">
          {t('ConfirmNewSalesTradingPhotoCardsPage.subTitleTradeMethod')}
        </p>
        <button
          type="button"
          className="flex items-center gap-[4px]"
          onClick={() => setIsDrawerOpen(true)}
        >
          <p className="text-[15px] font-medium text-text-main-lv3 leading-none">
            {t(`TradeMethod.${selectedTradeMethod || 'any'}`)}
          </p>
          <ArrowRight size={15} className="text-text-sub-lv1" />
        </button>
      </div>

      <BottomCTA
        className="mx-auto max-w-[var(--chakra-sizes-xl)]"
        onClick={onDone}
        disabled={isUploading || selectedPhotoCards.length === 0}
      >
        {t('done')}
      </BottomCTA>

      {isDrawerOpen && (
        <TradeMethodBottomDrawer
          user={user}
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          onSelectTradeMethod={(method: string) => {
            setSelectedTradeMethod(method);
            setIsDrawerOpen(false);
          }}
        />
      )}
    </main>
  );
};

export default SellPhotoCardsPage;
